import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { resolveWithdrawRowFees } from './walletFees';

// Convierte un entero a palabras en espanol (para la orden de pago).
const UNIDADES = ["", "UN", "DOS", "TRES", "CUATRO", "CINCO", "SEIS", "SIETE", "OCHO", "NUEVE"];
const DIEZ_DIECINUEVE = ["DIEZ", "ONCE", "DOCE", "TRECE", "CATORCE", "QUINCE", "DIECISEIS", "DIECISIETE", "DIECIOCHO", "DIECINUEVE"];
const DECENAS = ["", "", "VEINTE", "TREINTA", "CUARENTA", "CINCUENTA", "SESENTA", "SETENTA", "OCHENTA", "NOVENTA"];
const CENTENAS = ["", "CIENTO", "DOSCIENTOS", "TRESCIENTOS", "CUATROCIENTOS", "QUINIENTOS", "SEISCIENTOS", "SETECIENTOS", "OCHOCIENTOS", "NOVECIENTOS"];

function seccion(n) {
  let out = "";
  const c = Math.floor(n / 100);
  const d = Math.floor((n % 100) / 10);
  const u = n % 10;
  if (c > 0) out += (n === 100 ? "CIEN" : CENTENAS[c]) + " ";
  const resto = d * 10 + u;
  if (resto >= 10 && resto <= 19) {
    out += DIEZ_DIECINUEVE[resto - 10] + " ";
  } else {
    if (d === 2 && u > 0) {
      out += "VEINTI" + UNIDADES[u] + " ";
    } else {
      if (d > 0) out += DECENAS[d] + (u > 0 ? " Y " : " ");
      if (u > 0) out += UNIDADES[u] + " ";
    }
  }
  return out.trim();
}

export function numeroALetras(value) {
  let num = Math.floor(parseFloat(value || 0));
  if (num === 0) return "CERO PESOS M/CTE";
  let palabras = "";
  const millones = Math.floor(num / 1000000);
  const miles = Math.floor((num % 1000000) / 1000);
  const cientos = num % 1000;
  if (millones > 0) {
    palabras += (millones === 1 ? "UN MILLON" : seccion(millones) + " MILLONES") + " ";
  }
  if (miles > 0) {
    palabras += (miles === 1 ? "MIL" : seccion(miles) + " MIL") + " ";
  }
  if (cientos > 0) {
    palabras += seccion(cientos) + " ";
  }
  return palabras.trim() + " PESOS M/CTE";
}

function fmt(value, settings) {
  const number = parseFloat(value || 0);
  const decimal = settings && settings.decimal != null ? settings.decimal : 0;
  return number.toLocaleString("es-CO", { minimumFractionDigits: decimal, maximumFractionDigits: decimal });
}

function symbolize(value, settings) {
  const amount = fmt(value, settings);
  if (!settings) return amount;
  return settings.swipe_symbol ? amount + " " + (settings.symbol || "") : (settings.symbol || "") + " " + amount;
}

// Genera la orden de pago (PDF) para imprimir o subir al banco.
export function generateWithdrawPDF(entries, settings, t) {
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const tt = (k, d) => (t ? t(k, { defaultValue: d }) : d);

  const company = (settings && settings.CompanyName) || "MOVEX";
  const address = (settings && settings.CompanyAddress) || "";
  const phone = (settings && settings.CompanyPhone) || "";

  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text(company, 14, 16);
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  if (address) doc.text(String(address), 14, 22);
  if (phone) doc.text(tt("phone", "Tel") + ": " + String(phone), 14, 27);

  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text(tt("payment_order_title", "ORDEN DE PAGO - DISPERSION BANCARIA"), 14, 36);

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text(tt("date", "Fecha") + ": " + new Date().toLocaleString("es-CO"), 14, 42);

  const total = entries.reduce((acc, e) => acc + resolveWithdrawRowFees(e, settings).net, 0);

  const head = [[
    "#",
    tt("driver_name", "Beneficiario"),
    tt("document_id", "Documento"),
    tt("bankName", "Banco"),
    tt("account_type", "Tipo cuenta"),
    tt("bankAccount", "Numero de cuenta"),
    tt("amount", "Solicitado"),
    tt("withdraw_net", "Neto a transferir"),
  ]];

  const body = entries.map((e, i) => {
    const fees = resolveWithdrawRowFees(e, settings);
    return [
      String(i + 1),
      e.name || "",
      (e.documentType ? e.documentType + " " : "") + (e.documentId || ""),
      e.bankName || "",
      e.bankAccountType || "",
      e.bankAccount || "",
      symbolize(e.amount, settings),
      symbolize(fees.net, settings),
    ];
  });

  autoTable(doc, {
    head,
    body,
    startY: 48,
    styles: { fontSize: 9, cellPadding: 2 },
    headStyles: { fillColor: [20, 20, 20], textColor: 255 },
    columnStyles: { 6: { halign: "right" }, 7: { halign: "right" } },
  });

  const finalY = (doc.lastAutoTable ? doc.lastAutoTable.finalY : 48) + 10;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text(tt("total", "TOTAL") + ": " + symbolize(total, settings) + "   (" + entries.length + " " + tt("payments", "pagos") + ")", 14, finalY);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(tt("amount_in_words", "Son") + ": " + numeroALetras(total), 14, finalY + 6);

  doc.setFontSize(9);
  doc.text("____________________________", 14, finalY + 28);
  doc.text(tt("authorized_signature", "Firma autorizada"), 14, finalY + 33);

  doc.save("orden_pago_" + new Date().toISOString().slice(0, 10) + ".pdf");
}

// Codigos de tipo de cuenta por banco (convencion PAB/dispersion en Colombia).
const ACCOUNT_TYPE_CODE = {
  bancolombia: { Ahorros: "2", Corriente: "1" },
  davivienda: { Ahorros: "2", Corriente: "1" },
};

// Genera el archivo de dispersion (CSV ; ) para cargar en el banco y pagar automaticamente.
export function generateColombiaDispersionFile(entries, settings, bank) {
  const b = (bank || "bancolombia").toLowerCase();
  const codeMap = ACCOUNT_TYPE_CODE[b] || ACCOUNT_TYPE_CODE.bancolombia;
  const sep = ";";

  const header = [
    "TipoDocumento",
    "NumeroDocumento",
    "NombreBeneficiario",
    "CodigoBanco",
    "NombreBanco",
    "TipoCuenta",
    "TipoCuentaCodigo",
    "NumeroCuenta",
    "Valor",
    "Email",
    "Referencia",
  ];

  const rows = entries.map((e) => {
    const net = resolveWithdrawRowFees(e, settings).net;
    const valor = Math.round(parseFloat(net || 0));
    return [
      e.documentType || "CC",
      e.documentId || "",
      (e.name || "").replace(/[;\n\r]/g, " ").trim(),
      e.bankCode || "",
      (e.bankName || "").replace(/[;\n\r]/g, " ").trim(),
      e.bankAccountType || "",
      codeMap[e.bankAccountType] || "",
      e.bankAccount || "",
      String(valor),
      e.email || "",
      "Retiro Movex " + (e.id || ""),
    ].join(sep);
  });

  const content = [header.join(sep), ...rows].join("\r\n");
  const blob = new Blob(["\ufeff" + content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", "dispersion_" + b + "_" + new Date().toISOString().slice(0, 10) + ".csv");
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
